// Trim and pitch for sound files, done in the browser and baked into a new WAV that is uploaded like any
// other (the replaced file goes to the Archive tab, where it can be restored). Baking rather than storing
// the edit as settings means the game plays exactly what the editor previewed, and no client has to learn
// anything new. Pitch behaves like Unity's AudioSource.pitch: up is also faster, down is also slower.

export interface AudioEdit {
    /** Seconds into the source where the result begins. */
    start: number;
    /** Seconds into the source where the result ends; greater than start. */
    end: number;
    /** Pitch shift in semitones; 12 is an octave up, and twice as fast. */
    semitones: number;
}

/** What encodeWav needs; an AudioBuffer satisfies it. */
export interface PcmSource {
    numberOfChannels: number;
    sampleRate: number;
    length: number;
    getChannelData(channel: number): Float32Array;
}

// Short fades where a trim cuts into the waveform, so the new edges don't click.
const EDGE_FADE_SECONDS = 0.005;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function pitchRate(semitones: number): number {
    return Math.pow(2, semitones / 12);
}

/** Length of the edited sound in seconds. */
export function editedDuration(edit: AudioEdit): number {
    return Math.max(0, edit.end - edit.start) / pitchRate(edit.semitones);
}

/** True when the edit would give back the source unchanged. */
export function isIdentity(edit: AudioEdit, duration: number): boolean {
    return edit.semitones === 0 && edit.start <= 0 && edit.end >= duration;
}

/**
 * The sample rate in a WAV file's header, so decoding keeps it: an audio context resamples everything it
 * decodes to its own rate, which would quietly change the file on every edit. Undefined for anything that
 * isn't a readable RIFF/WAVE file.
 */
export function wavSampleRate(data: ArrayBuffer): number | undefined {
    const view = new DataView(data);
    const tag = (offset: number) => String.fromCharCode(
        view.getUint8(offset), view.getUint8(offset + 1), view.getUint8(offset + 2), view.getUint8(offset + 3));
    if (data.byteLength < 12 || tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return undefined;

    let offset = 12;
    while (offset + 8 <= data.byteLength) {
        const size = view.getUint32(offset + 4, true);
        if (tag(offset) === 'fmt ' && offset + 16 <= data.byteLength)
            return view.getUint32(offset + 12, true);
        offset += 8 + size + (size % 2); // chunks are word-aligned
    }
    return undefined;
}

/** Decodes a sound file at its own sample rate (44.1 kHz when the file doesn't say). */
export async function decodeAudio(data: ArrayBuffer): Promise<AudioBuffer> {
    const rate = wavSampleRate(data) ?? 44100;
    // An offline context decodes without a user gesture or an output device. decodeAudioData detaches the
    // buffer it is given, so it gets a copy and the caller's stays usable.
    const context = new OfflineAudioContext(1, 1, rate);
    return context.decodeAudioData(data.slice(0));
}

/** Renders the trimmed, pitched sound. The result has the source's channels and sample rate. */
export async function renderEdit(source: AudioBuffer, edit: AudioEdit): Promise<AudioBuffer> {
    const rate = pitchRate(edit.semitones);
    const start = clamp(edit.start, 0, source.duration);
    const end = clamp(edit.end, start, source.duration);
    const span = Math.max(end - start, 1 / source.sampleRate);
    const length = Math.max(1, Math.round(span / rate * source.sampleRate));
    const seconds = length / source.sampleRate;

    const context = new OfflineAudioContext(source.numberOfChannels, length, source.sampleRate);
    const node = context.createBufferSource();
    node.buffer = source;
    node.playbackRate.value = rate;

    const gain = context.createGain();
    const fade = Math.min(EDGE_FADE_SECONDS, seconds / 4);
    if (start > 0) {
        gain.gain.setValueAtTime(0, 0);
        gain.gain.linearRampToValueAtTime(1, fade);
    }
    if (end < source.duration) {
        gain.gain.setValueAtTime(1, seconds - fade);
        gain.gain.linearRampToValueAtTime(0, seconds);
    }

    node.connect(gain).connect(context.destination);
    // The third argument is seconds of SOURCE played, whatever the playback rate.
    node.start(0, start, span);
    return context.startRendering();
}

/** 16-bit PCM WAV, the format the game loads every sound in. */
export function encodeWav(source: PcmSource): Blob {
    const channels = source.numberOfChannels;
    const frames = source.length;
    const blockAlign = channels * 2;
    const dataSize = frames * blockAlign;
    const out = new ArrayBuffer(44 + dataSize);
    const view = new DataView(out);
    const writeTag = (offset: number, tag: string) => {
        for (let i = 0; i < tag.length; i++) view.setUint8(offset + i, tag.charCodeAt(i));
    };

    writeTag(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeTag(8, 'WAVE');
    writeTag(12, 'fmt ');
    view.setUint32(16, 16, true);              // fmt chunk size
    view.setUint16(20, 1, true);               // PCM
    view.setUint16(22, channels, true);
    view.setUint32(24, source.sampleRate, true);
    view.setUint32(28, source.sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, 16, true);              // bits per sample
    writeTag(36, 'data');
    view.setUint32(40, dataSize, true);

    const data: Float32Array[] = [];
    for (let c = 0; c < channels; c++) data.push(source.getChannelData(c));
    let offset = 44;
    for (let i = 0; i < frames; i++) {
        for (let c = 0; c < channels; c++) {
            const s = clamp(data[c][i], -1, 1);
            view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
            offset += 2;
        }
    }
    return new Blob([out], {type: 'audio/wav'});
}

/** The loudest sample in each of `buckets` equal slices, across all channels: what a waveform draws. */
export function peaks(source: PcmSource, buckets: number): number[] {
    const result = new Array<number>(Math.max(0, buckets)).fill(0);
    if (buckets <= 0 || source.length === 0) return result;
    const perBucket = source.length / buckets;
    for (let c = 0; c < source.numberOfChannels; c++) {
        const data = source.getChannelData(c);
        for (let b = 0; b < buckets; b++) {
            const from = Math.floor(b * perBucket);
            const to = Math.min(source.length, Math.max(from + 1, Math.floor((b + 1) * perBucket)));
            let max = result[b];
            for (let i = from; i < to; i++) {
                const v = Math.abs(data[i]);
                if (v > max) max = v;
            }
            result[b] = max;
        }
    }
    return result;
}
